import type { IanaTimezone } from "@/shared/lib/timezone"

export const formatDateLocal = (date: Date): string => {
  if (isNaN(date.getTime())) {
    return ""
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

/**
 * Parse a datetime string that represents a UTC time into a Date.
 * If the string lacks a timezone (e.g., "2025-08-31T07:54:00"),
 * treat it as UTC by appending 'Z'. If it already contains a
 * timezone (Z or +/-offset), use it as-is.
 */
export const parseUtcDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value

  const raw = value
  const hasTZ = /Z|([+-]\d{2}:?\d{2})$/.test(raw)
  const iso = hasTZ ? raw : `${raw}Z`
  const d = new Date(iso)
  return isNaN(d.getTime()) ? null : d
}

/**
 * Parse a date-only string (YYYY-MM-DD) as a local date without timezone conversion.
 * Use this for dates that represent calendar days (statistics periods, event dates)
 * rather than timestamps. This prevents timezone shifts that would show the wrong date.
 *
 * @param dateStr Date string in YYYY-MM-DD format
 * @returns Date object at local midnight, or null if invalid
 *
 * @example
 * // Backend sends: "2025-01-01" (stats for Jan 1 UTC)
 * const date = parseLocalDate("2025-01-01")
 * // All users see: "Jan 1" (no timezone conversion)
 */
export const parseLocalDate = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) return null

  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null

  const [, year, month, day] = match
  const date = new Date(Number(year), Number(month) - 1, Number(day))

  return isNaN(date.getTime()) ? null : date
}

/**
 * Compute the UTC offset in minutes for a given IANA timezone at a reference date.
 * Positive = ahead of UTC (e.g. +120 for UTC+02:00), negative = behind.
 * Normalised to the [-720, +840] range (UTC-12 to UTC+14).
 */
const getNumericDateTimePart = (
  parts: Intl.DateTimeFormatPart[],
  partType: "year" | "month" | "day" | "hour" | "minute" | "second",
  timezone: string
): number => {
  const rawValue = parts.find(part => part.type === partType)?.value
  if (!rawValue) {
    throw new Error(`Missing "${partType}" part for timezone "${timezone}"`)
  }

  const parsedValue = Number.parseInt(rawValue, 10)
  if (Number.isNaN(parsedValue)) {
    throw new Error(`Invalid "${partType}" value "${rawValue}" for timezone "${timezone}"`)
  }

  return parsedValue
}

const formatTime = (hours: number, minutes: number): string =>
  `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`

const timezoneOffsetFormatterCache = new Map<string, Intl.DateTimeFormat>()

const getTimezoneOffsetFormatter = (timezone: IanaTimezone | string): Intl.DateTimeFormat => {
  const cachedFormatter = timezoneOffsetFormatterCache.get(timezone)
  if (cachedFormatter) return cachedFormatter

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    hourCycle: "h23"
  })

  timezoneOffsetFormatterCache.set(timezone, formatter)
  return formatter
}

export const getTimezoneOffsetMinutes = (timezone: IanaTimezone | string, referenceDate: Date = new Date()): number => {
  const tzParts = getTimezoneOffsetFormatter(timezone).formatToParts(referenceDate)

  const year = getNumericDateTimePart(tzParts, "year", timezone)
  const month = getNumericDateTimePart(tzParts, "month", timezone)
  const day = getNumericDateTimePart(tzParts, "day", timezone)
  const hour = getNumericDateTimePart(tzParts, "hour", timezone)
  const minute = getNumericDateTimePart(tzParts, "minute", timezone)
  const second = getNumericDateTimePart(tzParts, "second", timezone)

  const zonedTimestamp = Date.UTC(year, month - 1, day, hour, minute, second)
  let offsetMin = Math.round((zonedTimestamp - referenceDate.getTime()) / 60000)

  if (offsetMin > 840) offsetMin -= 1440
  if (offsetMin < -720) offsetMin += 1440

  return offsetMin
}

/**
 * Format a timezone offset in minutes as a "UTC±HH:MM" label.
 */
export const formatUtcOffsetLabel = (offsetMin: number): string => {
  const sign = offsetMin >= 0 ? "+" : "-"
  const absMin = Math.abs(offsetMin)
  const h = String(Math.floor(absMin / 60)).padStart(2, "0")
  const m = String(absMin % 60).padStart(2, "0")
  return `UTC${sign}${h}:${m}`
}

export const getTimezoneDisplayLabel = (timezone?: IanaTimezone | null, referenceDate?: Date | null): string | null => {
  if (!timezone) return null

  try {
    const offsetMinutes = getTimezoneOffsetMinutes(timezone, referenceDate ?? new Date())
    const offset = offsetMinutes === 0 ? "UTC" : formatUtcOffsetLabel(offsetMinutes)
    return timezone === "UTC" ? "UTC" : `${timezone} · ${offset}`
  } catch (error) {
    console.error(`Error formatting timezone label for "${timezone}":`, error)
    return timezone === "UTC" ? "UTC" : null
  }
}

/**
 * Convert a time string ("HH:mm" or "HH:mm:ss") in UTC to a target timezone string ("HH:mm")
 * When timezone is provided, converts to that IANA timezone.
 * When timezone is omitted/null, uses UTC for group settings.
 * @param utcTime Time string in UTC (e.g., "09:00" or "09:00:00" means 09:00 UTC)
 * @param timezone Optional IANA timezone identifier (e.g., "Europe/Kyiv")
 * @returns Time string in the target timezone (e.g., "11:00")
 */
export const utcTimeToLocal = (utcTime: string, timezone?: IanaTimezone | null): string => {
  if (!utcTime) return ""

  const match = utcTime.match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d{1,6})?)?$/)
  if (!match) return utcTime

  const h = parseInt(match[1], 10)
  const m = parseInt(match[2], 10)

  const today = new Date()
  const utcDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), h, m, 0, 0))

  if (timezone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-GB", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      })
      return formatter.format(utcDate)
    } catch (error: unknown) {
      console.error(`Invalid timezone "${timezone}" in utcTimeToLocal:`, error)
      throw new Error(`Cannot convert UTC time to local with timezone "${timezone}"`)
    }
  }

  return formatTime(utcDate.getUTCHours(), utcDate.getUTCMinutes())
}

/**
 * Convert a local/group time string ("HH:mm" or "HH:mm:ss") to UTC time string ("HH:mm")
 * When timezone is provided, treats localTime as being in that IANA timezone.
 * When timezone is omitted/null, treats localTime as UTC for group settings.
 * @param localTime Time string in the source timezone (e.g., "12:00")
 * @param timezone Optional IANA timezone identifier (e.g., "Europe/Kyiv")
 * @returns UTC time string (e.g., "10:00")
 */
export const localTimeToUtc = (localTime: string, timezone?: IanaTimezone | null): string => {
  if (!localTime) return ""

  const match = localTime.match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d{1,6})?)?$/)
  if (!match) return localTime

  const h = parseInt(match[1], 10)
  const m = parseInt(match[2], 10)

  if (timezone) {
    try {
      const offsetMinutes = getTimezoneOffsetMinutes(timezone)

      let totalMinutes = h * 60 + m - offsetMinutes
      totalMinutes = ((totalMinutes % 1440) + 1440) % 1440

      const resultH = Math.floor(totalMinutes / 60)
      const resultM = totalMinutes % 60
      return formatTime(resultH, resultM)
    } catch (error: unknown) {
      console.error(`Invalid timezone "${timezone}" in localTimeToUtc:`, error)
      throw new Error(`Cannot convert local time to UTC with timezone "${timezone}"`)
    }
  }

  return formatTime(h, m)
}
